import {Avatar} from "@mantine/core";
import {type KeyboardEvent, useCallback} from "react";
import {useHandleTypeaheadQuery} from "../hooks/useHandleTypeahead";

export function HandleTypeahead(props: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const {value, onChange, onSubmit, disabled, placeholder} = props;

  const {data} = useHandleTypeaheadQuery(value);

  const firstActor = data?.actors[0];
  const ghostSuggestion = firstActor?.handle ?? "";
  const avatarUrl = firstActor?.avatar;
  const showGhost =
    ghostSuggestion.length > 0 &&
    value.length > 0 &&
    ghostSuggestion.toLowerCase().startsWith(value.toLowerCase()) &&
    ghostSuggestion.toLowerCase() !== value.toLowerCase();

  const isConfirmed = ghostSuggestion.toLowerCase() === value.toLowerCase();

  const ghostSuffix = showGhost ? ghostSuggestion.slice(value.length) : "";

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab" && showGhost) {
        e.preventDefault();
        onChange(ghostSuggestion);
      } else if (e.key === "Enter") {
        e.preventDefault();
        onSubmit();
      }
    },
    [showGhost, ghostSuggestion, onChange, onSubmit],
  );

  return (
    <div style={{position: "relative", width: "100%"}}>
      {showGhost && (
        <div
          aria-hidden="true"
          style={{
            display: "flex",
            justifyContent: "center",
            position: "absolute",
            margin: "1px",
            padding: "8px 12px 8px 42px",
            pointerEvents: "none",
            fontSize: "16px",
            lineHeight: 1.5,
            fontFamily: "monospace",
            opacity: 0.4,
          }}
        >
          <span style={{visibility: "hidden", whiteSpace: "pre"}}>{value}</span>
          <span style={{flex: "1 1", marginRight: "4px"}}>{ghostSuffix}</span>
          <span
            style={{display: "block"}}
            role="img"
            aria-label="Press Tab to accept completion"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <title>Press tab to accept completion</title>
              <line
                x1="19"
                y1="6"
                x2="19"
                y2="18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
              <line
                x1="4"
                y1="12"
                x2="17"
                y2="12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M12 7L17 12L12 17"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </div>
      )}
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        style={{
          display: "block",
          width: "100%",
          padding: "8px 12px 8px 42px",
          fontSize: "16px",
          lineHeight: 1.5,
          color: "var(--mantine-color-text)",
          backgroundColor: "var(--mantine-color-body)",
          border: "1px solid var(--mantine-color-default-border)",
          borderRadius: "var(--mantine-radius-md)",
          fontFamily: "monospace",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          left: "8px",
          transform: "translateY(-50%)",
          opacity: isConfirmed ? 1 : 0.5,
        }}
      >
        {avatarUrl ? (
          <Avatar
            alt={`Avatar for user ${ghostSuggestion}`}
            src={avatarUrl}
            size="sm"
            color="teal"
            radius="xl"
          />
        ) : (
          <Avatar
            alt="Empty avatar icon"
            name={ghostSuggestion || "@"}
            size="sm"
            color="teal"
            radius="xl"
          />
        )}
      </div>
    </div>
  );
}
